import {
  CapsuleGrid,
  Empty,
  Eyebrow,
  Page,
  PageBackdrop,
  POSITIONING,
  StoreHero,
  TAGLINE,
  ui,
} from '../ui';
import { toCapsule, useShelf } from '../catalogue';
import { useDocumentTitle } from '../title';
import { Loading } from './shared';

/**
 * The front page, which is the catalogue.
 *
 * **There used to be a separate home at `/` and there is not any more.** It was
 * `store-home.tsx`: the same shelf, cut to the newest twelve, under a hero and
 * a rail with a `See every game` link pointing here. Two pages drawing the same
 * grid from the same query, one of them a strictly smaller version of the
 * other, and the only thing between them was a limit.
 *
 * That shape earns its keep on a store with hundreds of titles, where a front
 * page is an editorial selection and browse is the index. This catalogue is a
 * studio's — tens of titles at most — so the selection was the whole list with
 * a number on it, and the front page's job was to link to the page that already
 * showed everything. Now the front page *is* that page.
 *
 * What has deliberately not been added: fake capsule art, an invented
 * "trending" rail, or a games-shipped figure padded to look healthier. With an
 * empty catalogue this shows the positioning line and one honest sentence,
 * because that is the true state and section 29.3 is explicit that a demo needs
 * a seed script rather than a lying UI. A studio with nothing out yet saying so
 * plainly is a great deal less embarrassing than one with an invented back
 * catalogue.
 *
 * The lead title stays in the grid below, unlike a hero that is sliced out of
 * its list. This is the page that claims to be every game we have, and a title
 * silently missing from it because it happened to be the newest makes that
 * claim false — the count in the eyebrow would disagree with the number of
 * capsules under it.
 *
 * `/browse` still resolves and redirects here, because the path is in links,
 * bookmarks and anywhere else nobody can edit. See `router.tsx`.
 */
export function BrowseRoute(): React.ReactElement {
  useDocumentTitle('Our games');

  /*
   * No limit, and there is no longer a shorter version of this query anywhere.
   *
   * `useShelf()` sends no `?limit=`, so the API answers with its own default of
   * sixty and clamps anything larger. That ceiling is the honest state of this
   * page: a studio's catalogue is tens of titles, and the day it is not, this is
   * where pagination goes rather than a number quietly raised until the response
   * is slow.
   */
  const query = useShelf();
  const entries = query.data?.entries ?? [];
  const titles = entries.map(toCapsule);

  /*
   * The most recently released title leads. Not "featured": nobody has paid for
   * placement, and there is no paid placement to sell (section 2). Making every
   * title here does not create one either — the order is still the release
   * date, because a house pick dressed as an editorial choice is the same lie
   * whether or not the money is internal.
   */
  const lead = titles[0] ?? null;

  return (
    <Page
      site="store"
      backdrop={<PageBackdrop imageUrl={lead?.pageBackgroundUrl ?? null} />}
      bleed={<StoreHero title={lead} tall />}
    >
      <header className={ui.stack ?? ''}>
        {/*
          A count and the word `games`, and nothing claiming they are all out.

          The grid carries coming-soon titles since #138, so `published` over it
          would be a figure disagreeing with what is under it, which is the one
          thing a count on this site may never do. `listed` said that correctly
          and said it in warehouse English; dropping the qualifier says the same
          thing to somebody who has never heard of a listing.
        */}
        <Eyebrow>
          {titles.length === 0
            ? 'Our games'
            : `${titles.length.toLocaleString('en-GB')} ${titles.length === 1 ? 'game' : 'games'}`}
        </Eyebrow>

        {/*
          The headline says what the company is when there is nothing to show,
          and names the page when there is.

          This is the half the old front page carried and the reason it is worth
          keeping: a visitor arriving at an empty catalogue should read what
          GOODBUILD is rather than the word `Games` over a dashed box. Once a
          title exists the grid says what we are far better than a sentence
          does, and the heading goes back to labelling the page.
        */}
        {titles.length === 0 ? (
          /*
            The display cut rather than the headline cut, and only in this
            branch.

            An empty catalogue means this line is the entire first impression of
            the company, and it was set two steps down from the largest type the
            system has — on a page with nothing under it but one sentence and a
            dashed box. Section 08 is explicit that loud is allowed and that big
            type is how: a positioning line at 96px on an otherwise empty screen
            is a title card, and a title card is a much better answer to *there
            is nothing here yet* than a small heading with a lot of room round
            it.

            The other branch stays at headline size, because there it is
            labelling a grid rather than opening a page. `POSITIONING` is three
            words, which is what makes it hold at this size — see
            `@goodbuild/rules/site`.
          */
          <>
            <h1 className="gb-display">{POSITIONING}</h1>
            <p className="gb-lead">{TAGLINE}</p>
          </>
        ) : (
          <h1 className="gb-headline">Our games</h1>
        )}
      </header>

      {query.isPending ? (
        <Loading label="Loading our games" />
      ) : query.error ? (
        <Empty>{query.error.message}</Empty>
      ) : titles.length === 0 ? (
        <Empty>No games yet. They turn up here as we finish them.</Empty>
      ) : (
        <CapsuleGrid titles={titles} />
      )}
    </Page>
  );
}
